import type {
  AdminCommentStatus,
  DashboardMetric,
  DashboardPeriod,
  PaymentStatus,
  SubscriptionStatus,
  UtmSelection,
} from './types';

export type AdminTab = 'dashboard' | 'subscriptions' | 'content' | 'products' | 'schedule';

/** Which dashboard drill-in modal is open, so it reopens on back-navigation. */
export type DashboardModalState =
  | { kind: 'members'; metric: DashboardMetric; title: string }
  | {
      kind: 'payments';
      status: 'succeeded' | 'refunded';
      title: string;
      // Set when the list is a tariff/source drill-in from the product modal.
      productId?: string;
      tariffId?: string;
      source?: string;
      // Drill-in зі звіту по мітках: один вимір + одне значення.
      utmDim?: 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm';
      utmValue?: string;
    }
  | { kind: 'popular' }
  | { kind: 'activity'; date: string; weekday: string }
  | { kind: 'tariffs'; productId: string; title: string };
export type DateOp = 'before' | 'after' | 'on' | 'between';
export type DateFilter = { op: DateOp; date1: string; date2?: string };
export type TextOp =
  | 'empty'
  | 'notEmpty'
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEquals';
export type TextFilter = { op: TextOp; value?: string };
export function textOpNeedsValue(op: TextOp): boolean {
  return op !== 'empty' && op !== 'notEmpty';
}

/**
 * Module-level mutable cache for the admin dashboard. Lives outside the
 * component lifecycle so navigating to a contact detail page and back
 * preserves the user's filter selection (and which tab they were on).
 *
 * The dashboard component reads these on mount, seeds its local $state,
 * and writes back on every change via $effect.
 */
export const adminState: {
  activeTab: AdminTab;
  subsSearch: string;
  subsStatusFilter: SubscriptionStatus[];
  subsTariffFilter: string[];
  subsStartFilter: DateFilter | null;
  subsEndFilter: DateFilter | null;
  subsEmailFilter: TextFilter | null;
  subsPhoneFilter: TextFilter | null;
  subsUsernameFilter: TextFilter | null;
  /** First-touch UTM контакту — мульти-селект по кожному виміру. */
  subsUtmFilter: UtmSelection;
  contentSubTab: 'categories' | 'materials';
  // Екран «Коментарі» (окремий роут) — фільтри переживають перехід у матеріал
  // і назад, як і в решти адмін-екранів.
  commentsSearch: string;
  commentsStatus: AdminCommentStatus;
  /** Непорожній = список запінений на один матеріал (перехід зі сторінки уроку). */
  commentsMaterialId: string;
  productsSearch: string;
  productsScopeFilter: 'all' | 'selected' | null;
  dashboardPeriod: DashboardPeriod;
  dashboardFrom: string;
  dashboardTo: string;
  dashboardPickYear: number;
  dashboardPickMonth: number;
  dashboardPickDay: string; // local ISO YYYY-MM-DD for the day-step period
  dashboardProduct: string;
  dashboardModal: DashboardModalState | null;
  // «Активність» page (separate route) — persisted so navigating into a contact
  // and back keeps the selected month and the open day drill-in.
  activityMonth: string; // YYYY-MM
  activityDay: string | null; // YYYY-MM-DD of the open day modal, or null
  // Payments screen filters — persisted so a drill-in + back keeps them.
  paymentsSearch: string;
  paymentsStatusFilter: PaymentStatus[];
  paymentsProductFilter: string;
  paymentsSourceFilter: string[];
  /** UTM-мітки самої оплати — мульти-селект по кожному виміру. */
  paymentsUtmFilter: UtmSelection;
  paymentsDateOp: '' | DateOp;
  paymentsDate1: string;
  paymentsDate2: string;
  /** Вибраний вимір у розділі «UTM» на дашборді. */
  dashboardUtmDim: 'source' | 'medium' | 'campaign' | 'content' | 'term';
} = {
  activeTab: 'dashboard',
  subsSearch: '',
  subsStatusFilter: [],
  subsTariffFilter: [],
  subsStartFilter: null,
  subsEndFilter: null,
  subsEmailFilter: null,
  subsPhoneFilter: null,
  subsUsernameFilter: null,
  subsUtmFilter: {},
  contentSubTab: 'materials',
  commentsSearch: '',
  commentsStatus: 'all',
  commentsMaterialId: '',
  productsSearch: '',
  productsScopeFilter: null,
  dashboardPeriod: 'month',
  dashboardFrom: '',
  dashboardTo: '',
  dashboardPickYear: new Date().getFullYear(),
  dashboardPickMonth: new Date().getMonth(),
  dashboardPickDay: (() => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })(),
  dashboardProduct: '',
  dashboardModal: null,
  activityMonth: (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })(),
  activityDay: null,
  paymentsSearch: '',
  paymentsStatusFilter: [],
  paymentsProductFilter: '',
  paymentsSourceFilter: [],
  paymentsUtmFilter: {},
  paymentsDateOp: '',
  paymentsDate1: '',
  paymentsDate2: '',
  dashboardUtmDim: 'source',
};
